import { Component, OnInit } from '@angular/core';
import { Course, Union } from '../../types/models';
import { User } from '../../types/auth';
import { CoreService } from '../../services/core.service';
import { UnionService } from '../../services/union.service';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import jspdf from 'jspdf';
import html2canvas from 'html2canvas';

export interface Certificate {
  union: Union;
  course: Course;
  user: User;
  completion_date: string;
  certificate_mode: string;
}
@Component({
  selector: 'app-certificate',
  templateUrl: './certificate.component.html',
  styleUrls: ['./certificate.component.scss'],
})
export class CertificateComponent implements OnInit {
  public certificate: Certificate | undefined;
  public loading = true;
  public certificateDefaultMode = true;
  constructor(
    private coreService: CoreService,
    private unionService: UnionService,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService
  ) {}

  /**
   * Partner / whitelabel logo for the subdomain the user is on (UnionService),
   * falling back to the union snapshot stored on the certificate.
   * Matches co-branding on V1 and fixes V2 when cert.union.logo_url is empty.
   */
  get whitelabelLogoUrl(): string {
    const fromTenant = this.unionService.union?.logo_url?.trim();
    const fromCert = this.certificate?.union?.logo_url?.trim();
    return (fromTenant || fromCert || '').trim();
  }

  get whitelabelLogoTitle(): string {
    return (
      this.unionService.union?.title ||
      this.certificate?.union?.title ||
      'Partner logo'
    );
  }

  /** Hide partner logo container on image error (V1: .union, V2: .certificate-v2-header). */
  onPartnerLogoError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    const wrap = img?.parentElement;
    if (!wrap) {
      return;
    }
    if (wrap.classList.contains('union-logo-v2')) {
      wrap.parentElement?.style.setProperty('display', 'none');
    } else {
      wrap.style.display = 'none';
    }
  }

  ngOnInit(): void {
    // Retrieve the slug from the URL
    const snapshot = this.activatedRoute.snapshot;
    const slug: string | null = snapshot.paramMap.get('slug');
    if (slug) {
      this.getCertificate(slug);
    }
  }

  public getCertificate(slug: string) {
    this.coreService.getCertificate(slug, {}).subscribe(
      (data) => {
        this.certificate = data;
        this.certificateDefaultMode = data.course.certificate_mode === 'V1';
        this.translate.use(this.certificate.course.language);
        this.loading = false;
      },
      (error) => {
        console.log(error);
        this.loading = false;
      }
    );
  }

  public format(dateString: string): string {
    const date = new Date(Date.parse(dateString));
    const intl = new Intl.DateTimeFormat(this.certificate?.course.language, {
      dateStyle: 'long',
    });
    return intl.format(date);
  }

  /**
   * html2canvas omits cross-origin images (e.g. union logo from API :8000 while SPA is :4200)
   * unless they are same-origin or inlined. Fetch partner/media URLs as blob and swap src briefly.
   */
  public async downloadPDF(): Promise<void> {
    const id = this.certificateDefaultMode ? 'certificate' : 'certificateV2';
    const data = document.getElementById(id);
    if (!data) {
      return;
    }

    type Restore = () => void;
    const restores: Restore[] = [];

    try {
      const images = Array.from(data.querySelectorAll('img')) as HTMLImageElement[];
      for (const img of images) {
        let absolute: URL;
        try {
          absolute = new URL(img.currentSrc || img.src, document.baseURI);
        } catch {
          continue;
        }
        if (absolute.origin === window.location.origin) {
          continue;
        }
        const originalSrc = img.src;
        try {
          const res = await fetch(absolute.href, {
            mode: 'cors',
            credentials: 'omit',
          });
          if (!res.ok) {
            continue;
          }
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          img.src = blobUrl;
          await new Promise<void>((resolve, reject) => {
            const t = window.setTimeout(
              () => reject(new Error('Image load timeout')),
              20000
            );
            img.onload = () => {
              window.clearTimeout(t);
              resolve();
            };
            img.onerror = () => {
              window.clearTimeout(t);
              reject(new Error('Image load error'));
            };
          });
          restores.push(() => {
            URL.revokeObjectURL(blobUrl);
            img.src = originalSrc;
          });
        } catch {
          // Keep original src; may still be missing in PDF if canvas taint rules apply
        }
      }

      const canvas = await html2canvas(data, {
        useCORS: true,
        allowTaint: false,
        scale: 2,
        logging: false,
        backgroundColor: null,
      });

      const a4Width = 297;
      const a4Height = 210;
      const imgWidth = 280;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const contentDataURL = canvas.toDataURL('image/png');
      const pdf = new jspdf('l', 'mm', 'a4');
      const xPos = (a4Width - imgWidth) / 2;
      const yPos = (a4Height - imgHeight) / 2;
      pdf.addImage(contentDataURL, 'PNG', xPos, yPos, imgWidth, imgHeight);
      pdf.save('certificate.pdf');
    } finally {
      restores.reverse().forEach((fn) => fn());
    }
  }
}
